import { memo, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

/**
 * Construye el JSX para un markdown determinado, con manejo especial
 * de enlaces de búsqueda que deben navegar al buscador del padre.
 */
/** Extrae el texto plano de cualquier nodo de React (string, array o elemento). */
function extractText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && node.props) return extractText(node.props.children);
  return "";
}

/** Indica si el texto del párrafo comienza con el carácter de viñeta •. */
function startsWithBullet(children) {
  return extractText(children).trimStart().startsWith("•");
}

/**
 * Elimina el carácter • (y espacios contiguos) del inicio del contenido.
 * Solo modifica el primer nodo de texto, dejando intactos los enlaces y demás.
 */
function stripLeadingBullet(children) {
  const childArray = Array.isArray(children) ? [...children] : [children];
  const firstIndex = childArray.findIndex(
    (child) => typeof child === "string" || typeof child === "number"
  );
  if (firstIndex === -1) return children;

  childArray[firstIndex] = String(childArray[firstIndex]).replace(/^\s*•\s*/, "");
  return childArray;
}

export function buildMarkdownJSX(markdown, parentBase) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={{
        p: ({ children, ...props }) => {
          const isBullet = startsWithBullet(children);
          const content = isBullet ? stripLeadingBullet(children) : children;
          return (
            <p {...props} className={isBullet ? "bullet-paragraph" : undefined}>
              {isBullet && <span className="bullet-marker" />}
              {content}
            </p>
          );
        },
        a: ({ href, children, className, ...props }) => {
          const safeHref = href || "";
          const isSearchLink = safeHref.includes("?q=") || safeHref.includes("&q=");
          const linkClassName = [
            className,
            isSearchLink ? "contenedor-ir-al-sitio" : null,
          ].filter(Boolean).join(" ") || undefined;
          const handleClick = (e) => {
            if (!isSearchLink) return;
            e.preventDefault();
            e.stopPropagation();
            const base = parentBase ? parentBase.replace(/\/+$/, "") : "";
            const query = safeHref.startsWith("?") ? safeHref : "?" + safeHref.split("?").pop();
            const fullUrl = base + "/buscador/" + query;
            try {
              if (window.top !== window.self) window.top.location.href = fullUrl;
              else window.location.href = fullUrl;
            } catch {
              window.parent.postMessage({ type: "navigate", url: fullUrl }, base || "*");
            }
          };
          return (
            <a
              {...props}
              className={linkClassName}
              href={safeHref}
              target="_top"
              rel="noreferrer"
              onClick={handleClick}
            >
              {children}
            </a>
          );
        },
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}

/**
 * Renderizado Markdown aislado para streaming.
 * El estado se actualiza solo en este componente, sin crear una raíz React
 * anidada ni provocar renders del historial completo.
 */
export const StreamingMarkdown = memo(({ targetRef, parentBase }) => {
  const [markdown, setMarkdown] = useState("");

  useEffect(() => {
    const targetElement = targetRef.current;
    if (!targetElement) return undefined;

    targetElement.__updateMarkdown = setMarkdown;
    return () => {
      delete targetElement.__updateMarkdown;
    };
  }, [targetRef]);

  return (
    <div className="summary-text" ref={targetRef}>
      {buildMarkdownJSX(markdown, parentBase)}
    </div>
  );
});
StreamingMarkdown.displayName = "StreamingMarkdown";

/**
 * Markdown estático (sin streaming).
 */
export const TextContent = memo(
  ({ markdown, parentBase }) => (
    <div className="summary-text">
      {buildMarkdownJSX(markdown, parentBase)}
    </div>
  ),
  (prev, next) => prev.markdown === next.markdown && prev.parentBase === next.parentBase
);
TextContent.displayName = "TextContent";